/*
  RENKA station: the home page as a Berlin U-Bahn station (three.js + GSAP), ported from
  docs/prototype/index.html. sections/renka-station.liquid renders every poster as real markup in the
  active language; this module turns that plain page into the 3D station when WebGL is available and
  falls back to the plain page if anything goes wrong.

  Works in both modes: carousel, add to cart (fetch to /cart/add.js), tester and newsletter sign-up
  (Shopify customer form, posted with fetch, native submit as fallback), live sign-up count (Section
  Rendering API, every 30 s while the shop poster is in view), #phase1/#phase2/#phase3 previews.
  3D only: scene, train, rail, input, mobile arrow, legal overlay.

  World layout numbers, timings and pixel effects are the prototype's; they were tuned by eye.
*/
import * as THREE from 'three';

const root = document.querySelector('.renka-station');
if (root) init(root);
// theme editor: Shopify re-renders the section when a setting changes; the scene is built once, so start over
document.addEventListener('shopify:section:load', (e) => { if (e.target.querySelector && e.target.querySelector('.renka-station')) location.reload(); });

function init(root) {
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => [...root.querySelectorAll(sel)];
  const I = JSON.parse($('[data-renka-i18n]').textContent);
  // Shopify HTML-escapes translations whose key does not end in _html; these strings go into textContent
  const decode = (s) => { const t = document.createElement('textarea'); t.innerHTML = s; return t.value; };
  const HTML_KEYS = ['ts1_live', 'ts2_live', 'ts3_live', 'ts1_consent', 'ts2_consent'];
  for (const k of Object.keys(I)) if (typeof I[k] === 'string' && !HTML_KEYS.includes(k)) I[k] = decode(I[k]);
  const fill = (s, vars) => String(s).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const clampT = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const is3d = () => root.classList.contains('is-3d');
  let active = false;               // true while the 3D station runs
  root.classList.add('rs-js');

  const posterEls = $$('.poster');
  const N = posterEls.length;
  const SHOP = posterEls.findIndex((el) => el.classList.contains('p-shop'));
  const postersLayer = $('.rs-posters');

  /* ---------------- Toast ---------------- */
  const toast = $('.rs-toast'); let toastTimer = 0;
  function say(msg) { toast.textContent = msg; toast.classList.add('on'); clearTimeout(toastTimer); toastTimer = setTimeout(() => toast.classList.remove('on'), 1800); }

  /* ---------------- Carousel (slides and carriages come from product.media) ---------------- */
  const track = $('.carousel .track'), carriagesEl = $('.carriages'), carousel = $('.carousel');
  const slides = track ? [...track.children] : [];
  let slide = 0;
  function showSlide(i) {
    if (!slides.length) return;
    slide = (i + slides.length) % slides.length;
    track.style.transform = `translateX(${-slide * 100}%)`;
    carriagesEl.querySelectorAll('.carriage').forEach((c, j) => c.setAttribute('aria-selected', String(j === slide)));
    track.querySelectorAll('video').forEach((v) => { const on = v.closest('.slide') === slides[slide]; on ? v.play().catch(() => {}) : v.pause(); });
  }
  if (carousel) {
    showSlide(0);
    carousel.addEventListener('click', (e) => {
      const step = e.target.closest('[data-slide]'); if (step) { showSlide(slide + +step.dataset.slide); return; }
      const to = e.target.closest('[data-to]'); if (to) showSlide(+to.dataset.to);
    });
    // plain page: swipe on the photo (the 3D station has its own touch handling)
    let sx = null;
    carousel.addEventListener('pointerdown', (e) => { if (!is3d() && e.pointerType !== 'mouse') sx = e.clientX; });
    carousel.addEventListener('pointerup', (e) => { if (sx === null) return; const dx = sx - e.clientX; sx = null; if (Math.abs(dx) > 30) showSlide(slide + Math.sign(dx)); });
    carousel.addEventListener('pointercancel', () => { sx = null; });
  }

  /* ---------------- Shopify forms: post with fetch, fall back to a normal submit ----------------
     Shopify protects customer forms with an invisible hCaptcha (captcha-bootstrap in content_for_header).
     On first focus it binds the form: from then on the submit event and form.submit() solve the captcha,
     add the h-captcha-response field and call the form's submit as it was at bind time. A post without that
     token is rejected ("Missing CAPTCHA token") and no customer is created. So the scripted post never runs
     next to Shopify's: form.submit is replaced before binding by `send`, which Shopify then calls with the
     token already in the form. If Shopify's internals change, its own normal submit (with token) still runs. */
  async function postForm(form, okParam) {
    const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } });
    const url = res.url || '';
    if (/\/challenge\b/.test(url)) return { challenge: true };
    if (url.includes(okParam)) return { ok: true };
    // Shopify's bot check can answer a scripted POST with a "Verifying your connection" page (403):
    // hand the form to the browser, which passes the check and reloads with ?customer_posted=true.
    if (!res.ok) return { challenge: true };
    return { ok: false, doc: new DOMParser().parseFromString(await res.text(), 'text/html') };
  }
  const captchaBound = (form) => form.hasAttribute('data-hcaptcha-bound');
  // Normal browser submit with a fresh captcha token (a token is single-use; the scripted post spent it).
  const nativeSubmit = (form) => {
    form.dataset.native = '1';
    if (form.elements['h-captcha-response']) form.elements['h-captcha-response'].remove();
    // Shopify ignores a second captcha submit within 250 ms of the first.
    if (captchaBound(form)) setTimeout(() => form.submit(), 300); else HTMLFormElement.prototype.submit.call(form);
  };
  // send: async, posts the form (with fetch). onWait(true/false): the captcha is loading / gave up.
  function captchaForm(form, send, onWait) {
    let busy = false, timer = 0;
    const run = () => {
      clearTimeout(timer); onWait(false);
      if (busy) return; busy = true;
      Promise.resolve(send()).finally(() => { busy = false; });
    };
    if (!captchaBound(form)) {
      form.submit = function () {
        if (form.dataset.native) HTMLFormElement.prototype.submit.call(form); else run();
      };
    }
    form.addEventListener('submit', (e) => {
      if (form.dataset.native) return;
      e.preventDefault();
      const cap = window.Shopify && window.Shopify.captcha;
      if (!cap || !cap.protect) { run(); return; }          // store without captcha, static preview
      if (captchaBound(form)) return;                       // Shopify's own listener runs next and calls send
      // Not bound yet (hCaptcha still loading): bind, then submit through the captcha.
      onWait(true);
      clearTimeout(timer);
      timer = setTimeout(() => onWait(false, true), 20000);  // hCaptcha blocked (tracking protection, ad blocker)
      cap.protect(form, () => form.submit());
    });
  }

  /* ---------------- Add to cart ---------------- */
  const addForm = $('.rs-add-form');
  if (addForm) {
    const cartLink = addForm.querySelector('.rs-cart-link');
    addForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = addForm.querySelector('.add'); btn.disabled = true;
      try {
        const res = await fetch(root.dataset.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(addForm) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || I.cart_error);
        const cart = await fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        say(fill(I.added, { n: cart.item_count }));
        if (cartLink) { cartLink.hidden = false; cartLink.textContent = fill(I.view_cart, { n: cart.item_count }); }
      } catch (err) {
        say(err && err.message ? err.message : I.cart_error);
      } finally { btn.disabled = false; }
    });
  }

  /* ---------------- Tester sign-up ----------------
     Until the shop opens, a bill is pasted over the shop poster. Phases by sign-up count: the first
     tester_spots become testers, the next first_batch are the first batch, everyone after that sees the
     waitlist grow. The count is shop.metafields.custom.renka_signup_count (Shopify Flow). Preview a phase with
     #phase1 / #phase2 / #phase3. */
  const testerEl = $('.tester');
  const SPOTS = +I.tester_spots, BATCH = +I.first_batch;
  const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';
  const hashId = location.hash.slice(1);
  let signups = +I.signups || 0, signedUp = false, doneName = '', shownPhase = +I.phase || 1;
  const hashPhase = () => +((/^#phase([123])$/.exec(location.hash) || [])[1] || 0);
  const phaseOf = (n) => hashPhase() || (n < SPOTS ? 1 : n < SPOTS + BATCH ? 2 : 3);
  const liveLine = (ph, preview) => (ph === 1 ? fill(I.ts1_live, { n: preview ? 7 : Math.max(0, SPOTS - signups) })
    : ph === 2 ? fill(I.ts2_live, { n: preview ? 37 : clampT(signups - SPOTS, 0, BATCH) })
    : fill(I.ts3_live, { n: preview ? 58 : Math.max(0, signups - SPOTS - BATCH) }));
  // Both customer forms report success through the same ?customer_posted=true; the hash says which one it was.
  const newsMsg = $('.news-msg');
  if (posted && hashId && hashId !== 'renka-newsletter' && newsMsg && newsMsg.textContent.trim() === I.news_ok) newsMsg.textContent = '';
  if (testerEl) {
    const tsDone = testerEl.querySelector('.ts-done');
    signedUp = !tsDone.hidden && !(posted && hashId && hashId !== 'renka-signup');
  }
  function renderTester() {
    if (!testerEl) return;
    const ph = phaseOf(signups), preview = hashPhase();
    shownPhase = ph;
    testerEl.querySelector('.ts-h').textContent = ph === 1 ? I.ts1_h : I.ts2_h;
    testerEl.querySelector('.ts-p').textContent = ph === 1 ? I.ts1_p : I.ts2_p;
    testerEl.querySelector('.ts-live').innerHTML = liveLine(ph, preview);
    testerEl.querySelector('.ts-consent-txt').innerHTML = ph === 1 ? I.ts1_consent : I.ts2_consent;
    const done = testerEl.querySelector('.ts-done');
    testerEl.querySelector('.ts-fields').hidden = signedUp; done.hidden = !signedUp;
    testerEl.querySelector('.ts-confirm').hidden = !signedUp;
    done.textContent = !signedUp ? '' : doneName ? fill(ph === 1 ? I.ts1_done : I.ts2_done, { name: doneName }) : (ph === 1 ? I.ts1_done_anon : I.ts2_done_anon);
  }
  addEventListener('hashchange', renderTester);
  renderTester();
  if (testerEl) {
    const form = testerEl.querySelector('form');
    const msg = testerEl.querySelector('.ts-msg'), btn = testerEl.querySelector('.ts-submit');
    captchaForm(form, async () => {
      const first = form.querySelector('[name="contact[first_name]"]').value.trim();
      btn.disabled = true; msg.textContent = '';
      try {
        const r = await postForm(form, 'customer_posted=true');
        if (r.challenge) { nativeSubmit(form); return; }
        if (r.ok) { signedUp = true; doneName = first; renderTester(); return; }
        const err = r.doc.querySelector('#renka-signup .ts-msg');
        if (err && err.textContent.trim()) { msg.innerHTML = err.innerHTML; return; }
        if (r.doc.querySelector('#renka-signup .ts-done:not([hidden])')) { signedUp = true; doneName = first; renderTester(); return; }
        // Neither an error nor a success page: let the browser send the form instead of showing a false error.
        nativeSubmit(form);
      } catch (err) {
        msg.textContent = I.ts_error;
      } finally { btn.disabled = false; }
    }, (waiting, failed) => {
      btn.disabled = waiting;
      if (failed) msg.textContent = I.form_captcha;
    });
  }

  /* ---------------- Newsletter (Endstation) ---------------- */
  const newsForm = $('.news');
  if (newsForm) {
    const btn = newsForm.querySelector('button');
    captchaForm(newsForm, async () => {
      btn.disabled = true;
      try {
        const r = await postForm(newsForm, 'customer_posted=true');
        if (r.challenge) { nativeSubmit(newsForm); return; }
        if (r.ok) { say(I.news_ok); newsMsg.textContent = I.news_ok; newsForm.reset(); return; }
        const err = r.doc.querySelector('#renka-newsletter .news-msg');
        if (err && err.textContent.trim() && err.textContent.trim() !== I.news_ok) { newsMsg.innerHTML = err.innerHTML; return; }
        if (err && err.textContent.trim() === I.news_ok) { say(I.news_ok); newsMsg.textContent = I.news_ok; newsForm.reset(); return; }
        nativeSubmit(newsForm);
      } catch (err) {
        newsMsg.textContent = I.ts_error;
      } finally { btn.disabled = false; }
    }, (waiting, failed) => {
      btn.disabled = waiting;
      if (failed) newsMsg.textContent = I.form_captcha;
    });
  }

  /* ---------------- Live count: re-render the section every 30 s while the shop poster is in view ---------------- */
  let shopVisible = false;
  let shopInView = () => shopVisible;
  if (testerEl && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => { shopVisible = entries.some((en) => en.isIntersecting); }).observe(testerEl);
  }
  async function poll() {
    if (!testerEl || document.hidden || !shopInView()) return;
    try {
      const id = root.dataset.sectionId;
      const res = await fetch(`${location.pathname}?sections=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const html = (await res.json())[id];
      if (!html) return;
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('.ts-live[data-signups]');
      const n = el ? parseInt(el.dataset.signups, 10) : NaN;
      if (Number.isFinite(n) && n !== signups) { signups = n; testerEl.querySelector('.ts-live').innerHTML = liveLine(shownPhase, hashPhase()); }
    } catch (err) { /* offline or preview: keep the last count */ }
  }
  if (testerEl) setInterval(poll, 30000);

  if (is3d()) {
    root.dataset.station = 'loading';
    station().catch(fallback);
  }

  /* ---------------- No WebGL or something broke: back to the plain page ---------------- */
  function fallback(err) {
    console.warn('[renka] 3D station unavailable, showing the plain page.', err);
    active = false;
    root.classList.remove('is-3d', 'is-booting', 'portrait');
    root.dataset.station = 'off';
    document.documentElement.style.overflow = ''; document.body.style.overflow = ''; document.documentElement.style.overscrollBehavior = '';
    $('.rs-stage').replaceChildren();
    posterEls.forEach((el) => { el.style.transform = ''; el.style.visibility = ''; });
    if (testerEl) { testerEl.style.transform = ''; testerEl.style.visibility = ''; }
    $$('.lofi').forEach((el) => { el.style.color = ''; el.style.backgroundImage = ''; el.style.fontSize = ''; });
    $('.rs-sheet').hidden = true;
  }

  /* ================================ 3D station ================================ */
  async function station() {
    const gsap = window.gsap;
    if (!gsap) throw new Error('GSAP did not load');
    const probe = document.createElement('canvas');
    if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) throw new Error('WebGL is not available');
    const INTERNAL_H = 448;                       // PS2-ish vertical resolution

    /* ---------------- Assets (theme files, URLs rendered by Liquid with asset_url) ---------------- */
    const D = root.dataset;
    const TEX = { ground: D.texGround, wall: D.texWall, edge: D.texEdge };

    /* ---------------- World layout (metres) ---------------- */
    const WALL_Z = -3.0;          // back wall across the tracks
    const TRACK_Z = 0;
    const EDGE_Z = 1.9;           // platform edge
    const POSTER_Y = 2.0;         // poster centre height
    const LAYOUTS = {
      landscape: { w: 4.4, h: 2.8, gap: 7.0, fov: 50, fitH: 0.80, fitW: 0.86, lift: 0.0 },
      portrait:  { w: 2.0, h: 3.2, gap: 4.0, fov: 62, fitH: 0.80, fitW: 0.88, lift: 0.035 },
    };
    const STATION = { pos: new THREE.Vector3(0, 1.7, 9.5), target: new THREE.Vector3(0, 1.55, 0) };

    let mode = innerWidth / innerHeight < 0.9 ? 'portrait' : 'landscape';
    let L = LAYOUTS[mode];
    root.classList.toggle('portrait', mode === 'portrait');
    const posterX = (i) => i * L.gap;

    /* ---------------- Renderer ---------------- */
    const stage = $('.rs-stage');
    const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(1);
    stage.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0e1012);
    scene.fog = new THREE.Fog(0x0e1012, 22, 75);
    const camera = new THREE.PerspectiveCamera(L.fov, 1, 0.1, 200);
    const camPos = STATION.pos.clone();
    const camTarget = STATION.target.clone();

    /* ---------------- Textures ---------------- */
    // Tileable 64px grime: speckled pixel blotches around mid-grey. One pixel = 3 poster px (CSS 192px tile).
    const GRIME_PX = 3;
    const grime = document.createElement('canvas'); grime.width = grime.height = 64;
    {
      const g = grime.getContext('2d'), S = 64, wrap = (v) => ((v % S) + S) % S;
      g.fillStyle = 'rgb(128,128,128)'; g.fillRect(0, 0, S, S);
      for (let i = 0; i < 900; i++) { const v = 112 + Math.random() * 32 | 0; g.fillStyle = `rgb(${v},${v},${v})`; g.fillRect(Math.random() * S | 0, Math.random() * S | 0, 1, 1); }
      for (let b = 0; b < 16; b++) {                       // blotches: darker stains and a few faded patches
        const cx = Math.random() * S, cy = Math.random() * S, r = 2 + Math.random() * 6;
        const v = Math.random() < 0.65 ? 70 + Math.random() * 30 : 165 + Math.random() * 25;
        g.fillStyle = `rgb(${v | 0},${v | 0},${v | 0})`;
        for (let y = -r; y <= r; y++) for (let x = -r; x <= r; x++) {
          const d = Math.hypot(x, y) / r;
          if (d < 1 && Math.random() > d * d) g.fillRect(wrap(Math.round(cx + x)), wrap(Math.round(cy + y)), 1, 1);
        }
      }
      for (let i = 0; i < 3; i++) {                        // a couple of scuffs
        let x = Math.random() * S, y = Math.random() * S; const dx = Math.random() * 2 - 1, dy = Math.random() * 0.6 - 0.3;
        g.fillStyle = 'rgb(96,96,96)';
        for (let k = 0; k < 14; k++) { g.fillRect(wrap(Math.round(x)), wrap(Math.round(y)), 1, 1); x += dx; y += dy; }
      }
    }
    root.style.setProperty('--grime', `url(${grime.toDataURL()})`);
    // Edge dirt: one canvas pixel = 3 poster px; dark pixels get denser towards the edges and densest in the corners
    function edgeDirt(w, h) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const g = c.getContext('2d');
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const edge = Math.min(x, y, w - 1 - x, h - 1 - y);
        const corner = Math.min(Math.hypot(x, y), Math.hypot(w - x, y), Math.hypot(x, h - y), Math.hypot(w - x, h - y));
        const p = 0.6 * Math.exp(-edge / 3.5) + 0.55 * Math.exp(-corner / 11) + (y > h - 12 ? 0.08 : 0);   // a little extra grime at the bottom
        if (Math.random() < p) { g.fillStyle = `rgba(38,30,22,${(0.06 + Math.random() * 0.18).toFixed(2)})`; g.fillRect(x, y, 1, 1); }
      }
      return c;
    }
    const EDGE = { landscape: edgeDirt(880 / GRIME_PX, 560 / GRIME_PX), portrait: edgeDirt(400 / GRIME_PX | 0, 640 / GRIME_PX | 0) };
    root.style.setProperty('--edge-l', `url(${EDGE.landscape.toDataURL()})`);
    root.style.setProperty('--edge-p', `url(${EDGE.portrait.toDataURL()})`);

    function canvasTex(w, h, draw, repeat) {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      draw(c.getContext('2d'), w, h);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      if (repeat) t.repeat.set(...repeat);
      return t;
    }
    // Images go into canvases and WebGL, so they load CORS-clean; a failed load gives a small transparent stand-in.
    const blank = () => { const c = document.createElement('canvas'); c.width = c.height = 2; return c; };
    const loadImg = (src) => new Promise((res) => {
      if (!src) { res(blank()); return; }
      const i = new Image(); i.crossOrigin = 'anonymous'; i.decoding = 'async';
      i.onload = () => res(i); i.onerror = () => res(blank()); i.src = src;
    });
    // Supplied textures, normalised to 64x64 and flattened over a backing colour (the edge PNG has stray transparent pixels).
    async function suppliedTex(src, back) {
      const img = await loadImg(src);
      return canvasTex(64, 64, (g, w, h) => { g.fillStyle = back; g.fillRect(0, 0, w, h); g.drawImage(img, 0, 0, w, h); });
    }
    const [texGround, texWall, texEdge] = await Promise.all([
      suppliedTex(TEX.ground, '#6b6b6b'), suppliedTex(TEX.wall, '#d98a4e'), suppliedTex(TEX.edge, '#464646'),
    ]);
    const texGravel = canvasTex(64, 64, (g, w, h) => {
      g.fillStyle = '#2a2724'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 500; i++) { const v = 40 + Math.random() * 50 | 0; g.fillStyle = `rgb(${v + 6},${v},${v - 6})`; g.fillRect(Math.random() * w, Math.random() * h, 2, 2); }
    });
    // Lower wall: grimy grey render, no tiles
    const texGrime = canvasTex(128, 64, (g, w, h) => {
      g.fillStyle = '#5b5d5c'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 900; i++) { const v = 70 + Math.random() * 40 | 0; g.fillStyle = `rgba(${v},${v},${v - 3},.5)`; g.fillRect(Math.random() * w, Math.random() * h, 1 + Math.random() * 2, 1); }
      for (let i = 0; i < 40; i++) { g.fillStyle = `rgba(28,26,22,${0.06 + Math.random() * 0.14})`; g.fillRect(Math.random() * w, 0, 1 + Math.random() * 2, h * (0.3 + Math.random() * 0.7)); }
      const soot = g.createLinearGradient(0, 0, 0, h); soot.addColorStop(0, 'rgba(20,18,16,.05)'); soot.addColorStop(1, 'rgba(20,18,16,.45)');
      g.fillStyle = soot; g.fillRect(0, 0, w, h);
    });
    // Train livery: Berlin yellow with grime, brake dust low down and run-off streaks
    const texTrain = canvasTex(128, 64, (g, w, h) => {
      g.fillStyle = '#e9cf1f'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 700; i++) { const a = Math.random() * 0.12; g.fillStyle = `rgba(60,48,10,${a})`; g.fillRect(Math.random() * w, Math.random() * h, 1 + Math.random() * 2, 1); }
      const dust = g.createLinearGradient(0, h * 0.55, 0, h);
      dust.addColorStop(0, 'rgba(70,52,20,0)'); dust.addColorStop(1, 'rgba(55,40,18,.55)');
      g.fillStyle = dust; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 26; i++) { g.fillStyle = `rgba(50,40,15,${0.08 + Math.random() * 0.12})`; g.fillRect(Math.random() * w, h * (0.15 + Math.random() * 0.3), 1, h * (0.2 + Math.random() * 0.5)); }
      g.fillStyle = 'rgba(30,24,10,.35)'; g.fillRect(0, 0, w, 3);
    });
    const texTrainDoor = canvasTex(32, 64, (g, w, h) => {
      g.drawImage(texTrain.image, 40, 0, 32, 64, 0, 0, w, h);
      g.fillStyle = 'rgba(30,24,10,.5)'; g.fillRect(w - 2, 0, 2, h);
      g.fillStyle = 'rgba(30,24,10,.25)'; g.fillRect(4, h * 0.62, w - 8, 2);
    });
    const texGlass = canvasTex(32, 16, (g, w, h) => {
      g.fillStyle = '#1a2328'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 60; i++) { g.fillStyle = `rgba(160,170,160,${Math.random() * 0.1})`; g.fillRect(Math.random() * w, Math.random() * h, 2, 1); }
      g.fillStyle = 'rgba(200,210,215,.08)'; g.beginPath(); g.moveTo(0, h); g.lineTo(w * 0.35, 0); g.lineTo(w * 0.5, 0); g.lineTo(w * 0.15, h); g.fill();
    });
    // Enamel sign in hard pixels: thick, slightly rounded black outline (its top edge catches a little light),
    // a panel shaded light at the top and darker towards the bottom, a drop shadow, and pixel lettering.
    function drawSign(g, w, h, { text, ink, shades, weight, R, B }) {
      const SH = 2, SW = w - SH, SHh = h - SH;
      const inRound = (i, j, x, y, ww, hh, r) => {
        if (i < x || j < y || i >= x + ww || j >= y + hh) return false;
        const lx = i - x, ly = j - y;
        const cx = lx < r ? r - lx - 0.5 : lx >= ww - r ? lx - (ww - r) + 0.5 : 0;
        const cy = ly < r ? r - ly - 0.5 : ly >= hh - r ? ly - (hh - r) + 0.5 : 0;
        return cx * cx + cy * cy <= r * r;
      };
      g.clearRect(0, 0, w, h);
      for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
        let c = null;
        if (inRound(i, j, 0, 0, SW, SHh, R)) {
          if (inRound(i, j, B, B, SW - 2 * B, SHh - 2 * B, Math.max(1, R - B))) {
            const t = (j - B) / (SHh - 2 * B - 1);
            c = t < 0.12 ? shades[0] : t > 0.86 ? shades[3] : t > 0.68 ? shades[2] : shades[1];
          } else c = j <= 1 ? '#3a3b40' : '#0b0b0c';
        } else if (inRound(i, j, SH, SH, SW, SHh, R)) c = 'rgba(0,0,0,.45)';
        if (c) { g.fillStyle = c; g.fillRect(i, j, 1, 1); }
      }
      const t = document.createElement('canvas'); t.width = w; t.height = h;
      const tg = t.getContext('2d');
      tg.fillStyle = ink; tg.font = `${weight} ${Math.round((SHh - 2 * B) * 0.84)}px "Barlow Condensed", Arial Narrow, sans-serif`;
      tg.textAlign = 'center'; tg.textBaseline = 'middle'; tg.fillText(text, SW / 2, SHh / 2 + 1);
      hardAlpha(tg, w, h);
      g.drawImage(t, 0, 0);
    }
    const drawName = (g, w, h) => drawSign(g, w, h, { text: 'RENKA', ink: '#16181b', shades: ['#ffffff', '#f3f1ea', '#d9d4c7', '#bdb8ab'], weight: 700, R: 8, B: 4 });
    const drawU = (g, w, h) => drawSign(g, w, h, { text: 'U', ink: '#ffffff', shades: ['#4a84d4', '#1b56a8', '#164a92', '#103a75'], weight: 800, R: 6, B: 3 });
    // Poster frame bars: brushed aluminium in big pixels. The grain runs along the bar
    // (32 px = 0.8 m along the length, 8 px across), lit from the top left.
    function frameTex(vertical) {
      const LEN = 32, TH = 8;
      const t = canvasTex(vertical ? TH : LEN, vertical ? LEN : TH, (g) => {
        const px = (a, b, c) => { g.fillStyle = c; vertical ? g.fillRect(b, a, 1, 1) : g.fillRect(a, b, 1, 1); };   // a = along, b = across
        for (let b = 0; b < TH; b++) {
          let v = 112 + Math.random() * 14;
          for (let a = 0; a < LEN; a++) {                     // streaks: brightness drifts slowly along the bar
            if (Math.random() < 0.18) v = 104 + Math.random() * 30;
            px(a, b, `rgb(${v | 0},${v + 3 | 0},${v + 8 | 0})`);
          }
        }
        for (let a = 0; a < LEN; a++) { px(a, 0, 'rgb(170,174,180)'); px(a, TH - 1, 'rgb(62,64,68)'); }   // bevel
        for (let i = 0; i < 5; i++) px(Math.random() * LEN | 0, 1 + Math.random() * (TH - 2) | 0, 'rgb(84,86,90)');   // scratches
        const r = Math.random() * (LEN - 3) | 0; px(r, TH - 2, 'rgb(92,70,48)'); px(r + 1, TH - 2, 'rgb(104,78,52)');   // rust
      });
      t.magFilter = THREE.NearestFilter;
      return t;
    }
    const texFrameH = frameTex(false), texFrameV = frameTex(true);
    const texName = canvasTex(128, 34, drawName);
    // Drop antialiasing from transparent canvases: every pixel fully on or off
    function hardAlpha(g, w, h) {
      const d = g.getImageData(0, 0, w, h);
      for (let i = 3; i < d.data.length; i += 4) d.data[i] = d.data[i] > 110 ? 255 : 0;
      g.putImageData(d, 0, 0);
    }
    // Wall graffiti, sprayed in hard pixels with a dark outline
    function graffitiTex(lines, fill, px = 22) {
      const c = document.createElement('canvas'), g = c.getContext('2d');
      const font = `400 ${px}px "Permanent Marker", "Comic Sans MS", cursive`;
      g.font = font;
      c.width = Math.ceil(Math.max(...lines.map((l) => g.measureText(l).width))) + 10; c.height = lines.length * px + 12;
      g.font = font; g.textBaseline = 'top'; g.lineJoin = 'round';
      g.strokeStyle = '#16181b'; g.lineWidth = 4; lines.forEach((l, i) => g.strokeText(l, 5, 5 + i * px));
      g.fillStyle = fill; lines.forEach((l, i) => g.fillText(l, 5, 5 + i * px));
      hardAlpha(g, c.width, c.height);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.magFilter = THREE.NearestFilter;
      return t;
    }
    // Red arrow sprayed on the tiles, pointing along `ang` (radians, world space: 0 = right, +π/2 = up), with a bit of
    // overspray and paint running down; hard pixels like the other wall graffiti
    function sprayArrow(ang, seed) {
      const N = 48, c = document.createElement('canvas'); c.width = c.height = N;
      const g = c.getContext('2d'), rnd = ((a) => () => (a = (a * 16807) % 2147483647) / 2147483647)(seed);
      const dx = Math.cos(ang), dy = -Math.sin(ang), cx = N / 2, cy = N / 2 - 3;
      const tip = [cx + dx * 15, cy + dy * 15], tail = [cx - dx * 15, cy - dy * 15];
      const head = (s) => { const a = Math.atan2(dy, dx) + s * 2.55; return [tip[0] + Math.cos(a) * 11, tip[1] + Math.sin(a) * 11]; };
      g.strokeStyle = g.fillStyle = 'rgb(222,44,38)'; g.lineWidth = 4.5; g.lineCap = g.lineJoin = 'round';
      g.beginPath(); g.moveTo(...tail); g.lineTo(...tip); g.moveTo(...head(1)); g.lineTo(...tip); g.lineTo(...head(-1)); g.stroke();
      for (let i = 0; i < 26; i++) {               // overspray
        const t = rnd(), r = 3 + rnd() * 4, a = rnd() * 6.283;
        g.fillRect(Math.round(tail[0] + (tip[0] - tail[0]) * t + Math.cos(a) * r), Math.round(tail[1] + (tip[1] - tail[1]) * t + Math.sin(a) * r), 1, 1);
      }
      for (let i = 0; i < 3; i++) {                // drips
        const t = 0.15 + rnd() * 0.7, x = Math.round(tail[0] + (tip[0] - tail[0]) * t), y = Math.round(tail[1] + (tip[1] - tail[1]) * t) + 1;
        g.fillRect(x, y, 2, 3 + Math.round(rnd() * 6));
      }
      hardAlpha(g, N, N);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.magFilter = THREE.NearestFilter;
      return t;
    }
    const mascotImg = await loadImg(D.texMascot);
    // Mascot sprayed onto the wall: coarser pixels, soft edges, overspray and a few drips
    function sprayMascot(img) {
      const lw = 24, lh = Math.round(lw * img.height / img.width), S = 8, M0 = 12;
      const lo = document.createElement('canvas'); lo.width = lw; lo.height = lh;
      const lg = lo.getContext('2d'); lg.drawImage(img, 0, 0, lw, lh);           // smooth downscale, then snap
      const d = lg.getImageData(0, 0, lw, lh), a = d.data;
      for (let i = 0; i < a.length; i += 4) {
        if (a[i + 3] < 70) { a[i + 3] = 0; continue; }
        const dark = a[i] + a[i + 1] + a[i + 2] < 420;
        a[i] = a[i + 1] = a[i + 2] = dark ? 20 : 242; a[i + 3] = 255;
      }
      lg.putImageData(d, 0, 0);
      const c = document.createElement('canvas'); c.width = lw * S + 2 * M0; c.height = lh * S + 2 * M0 + 26;
      const g = c.getContext('2d'); g.imageSmoothingEnabled = false;
      const blk = document.createElement('canvas'); blk.width = lw * S; blk.height = lh * S;
      const bg = blk.getContext('2d'); bg.imageSmoothingEnabled = false; bg.drawImage(lo, 0, 0, lw * S, lh * S);
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {   // soft edge: stacked offsets of the chunky image
        g.globalAlpha = 0.07; g.drawImage(blk, M0 + dx * 2, M0 + dy * 2);
      }
      g.globalAlpha = 0.75; g.drawImage(blk, M0, M0);
      g.globalAlpha = 1;
      for (let j = 0; j < lh; j++) for (let i = 0; i < lw; i++) {
        const k = (j * lw + i) * 4; if (!a[k + 3] || a[k] > 100) continue;
        for (let n = 0; n < 4; n++) {                                           // overspray dots around the black lines
          const r = 4 + Math.random() * 14, t = Math.random() * 6.28;
          g.fillStyle = `rgba(20,20,22,${(0.08 + Math.random() * 0.2).toFixed(2)})`;
          g.fillRect(M0 + i * S + S / 2 + Math.cos(t) * r, M0 + j * S + S / 2 + Math.sin(t) * r, 2, 2);
        }
        const below = ((j + 1) * lw + i) * 4;
        if ((j + 1 >= lh || !a[below + 3]) && Math.random() < 0.18) {          // drips from lower edges
          g.fillStyle = 'rgba(20,20,22,.7)'; g.fillRect(M0 + i * S + 3, M0 + (j + 1) * S, 2, 6 + Math.random() * 18);
        }
      }
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;   // linear filtering keeps the edges soft
      return t;
    }
    const texMascot = sprayMascot(mascotImg);
    // Supplied sprites plus a few small pixel details for the platform floor
    const spriteTex = (img) => { const t = new THREE.Texture(img); t.colorSpace = THREE.SRGBColorSpace; t.magFilter = THREE.NearestFilter; t.needsUpdate = true; return t; };
    const pxTex = (w, h, draw) => { const t = canvasTex(w, h, draw); t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.magFilter = THREE.NearestFilter; return t; };
    const [imgBottle, imgVomit, imgSticker, pieceImg, tagImg] = await Promise.all([loadImg(D.texBottle), loadImg(D.texVomit), loadImg(D.texSticker), loadImg(D.texPiece), loadImg(D.texTag)]);
    const texBottle = spriteTex(imgBottle), texVomit = spriteTex(imgVomit), texSticker = spriteTex(imgSticker), texRenkaWall = spriteTex(pieceImg);
    const texGum = pxTex(16, 16, (g) => {          // flattened chewing gum
      [[3, 4, 2], [10, 3, 1], [7, 10, 2], [13, 12, 1], [2, 13, 1]].forEach(([x, y, r]) => {
        for (let j = -r; j <= r; j++) for (let i = -r - 1; i <= r + 1; i++) if (i * i / ((r + 1) * (r + 1)) + j * j / (r * r || 1) <= 1) { g.fillStyle = (i + j) % 3 ? '#3e3e41' : '#55555a'; g.fillRect(x + i, y + j, 1, 1); }
      });
    });
    const texButt = pxTex(8, 3, (g) => { g.fillStyle = '#e9e4d8'; g.fillRect(0, 0, 6, 3); g.fillStyle = '#d4883a'; g.fillRect(5, 0, 3, 3); g.fillStyle = '#5a5552'; g.fillRect(0, 1, 1, 1); });
    const texTicket = pxTex(12, 8, (g) => {        // a dropped single ticket
      g.fillStyle = '#f4f1e6'; g.fillRect(0, 0, 12, 8); g.fillStyle = '#f0d722'; g.fillRect(0, 0, 12, 2);
      g.fillStyle = '#16181b'; g.fillRect(2, 4, 6, 1); g.fillRect(2, 6, 4, 1); g.fillStyle = '#1b56a8'; g.fillRect(9, 4, 2, 3);
    });
    const texCan = pxTex(10, 6, (g) => {           // crushed can
      g.fillStyle = '#b9bec5'; g.fillRect(0, 1, 10, 4); g.fillStyle = '#c8102e'; g.fillRect(2, 1, 5, 4);
      g.fillStyle = '#6f747b'; g.fillRect(4, 1, 1, 4); g.fillRect(0, 2, 1, 2); g.fillStyle = '#e6e9ee'; g.fillRect(1, 1, 8, 1);
    });
    // Station pictograms: white on blue enamel plates, drawn on a 32x32 grid and snapped to hard pixels
    const PLATE = '#1b4f9c', PAPER = '#f3f1ea';
    function pictoTex(draw) {
      const c = document.createElement('canvas'); c.width = c.height = 32;
      const g = c.getContext('2d');
      g.fillStyle = PLATE; g.fillRect(0, 0, 32, 32);
      g.strokeStyle = PAPER; g.lineWidth = 1; g.strokeRect(1.5, 1.5, 29, 29);
      g.fillStyle = PAPER; g.strokeStyle = PAPER; g.lineWidth = 2;
      draw(g);
      const d = g.getImageData(0, 0, 32, 32), a = d.data;
      for (let i = 0; i < a.length; i += 4) { const on = a[i] > 135; a[i] = on ? 243 : 27; a[i + 1] = on ? 241 : 79; a[i + 2] = on ? 234 : 156; a[i + 3] = 255; }
      g.putImageData(d, 0, 0);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.magFilter = THREE.NearestFilter; return t;
    }
    const line = (g, ...pts) => { g.beginPath(); g.moveTo(pts[0], pts[1]); for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]); g.stroke(); };
    const dot = (g, x, y, r) => { g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); };
    const PICTOS = {
      hang: pictoTex((g) => {                       // hanging from a ledge
        g.fillRect(5, 6, 22, 2); line(g, 10, 8, 12, 16); line(g, 22, 8, 20, 16); dot(g, 16, 11.5, 2.5);
        g.fillRect(13, 15, 6, 8); g.lineWidth = 2; line(g, 14, 23, 13, 28); line(g, 18, 23, 19, 28);
      }),
      clock: pictoTex((g) => {                      // stopwatch: short sessions
        g.beginPath(); g.arc(16, 18, 8, 0, Math.PI * 2); g.stroke(); g.fillRect(14, 6, 4, 3); g.fillRect(23, 9, 3, 2);
        line(g, 16, 18, 16, 12); line(g, 16, 18, 20, 20);
      }),
      bars: pictoTex((g) => { g.fillRect(6, 21, 5, 6); g.fillRect(13, 16, 5, 11); g.fillRect(20, 9, 5, 18); }),   // progress
      pack: pictoTex((g) => {                       // backpack: takes it anywhere
        g.fillRect(9, 11, 14, 16); g.beginPath(); g.arc(16, 11, 4, Math.PI, 0); g.stroke();
        g.fillStyle = PLATE; g.fillRect(11, 19, 10, 6); g.fillRect(11, 14, 10, 1);
      }),
      cart: pictoTex((g) => {
        line(g, 5, 7, 9, 7, 12, 19, 25, 19);
        g.beginPath(); g.moveTo(10, 10); g.lineTo(27, 10); g.lineTo(25, 17); g.lineTo(12, 17); g.closePath(); g.fill();
        dot(g, 14, 24, 2); dot(g, 23, 24, 2);
      }),
      train: pictoTex((g) => {
        g.fillRect(8, 6, 16, 17); g.fillStyle = PLATE; g.fillRect(10, 9, 12, 6); g.fillRect(10, 18, 3, 3); g.fillRect(19, 18, 3, 3);
        g.fillStyle = PAPER; g.fillRect(9, 25, 5, 2); g.fillRect(18, 25, 5, 2);
      }),
    };
    const PICTO_SLOTS = [[0, 'hang'], [1, 'clock'], [2, 'bars'], [3, 'pack'], [4, 'cart'], [5, 'train']];   // gap i = wall between poster i and i+1
    let floorTag = null;          // the "Mini Hangboards" piece sprayed on the platform floor (made with the train, placed per layout)
    const KEEP = new Set([texMascot, texBottle, texVomit, texSticker, texRenkaWall, texGum, texButt, texTicket, texCan, ...Object.values(PICTOS)]);   // shared, never disposed on rebuild
    // Litter where people wait, between the edge strip and the pillars: [texture, size m, x, z, rotation]
    const rng = ((a) => () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; })(42);
    const LITTER = {
      landscape: [[texBottle, 0.75, -2.3, 3.7, 0.4], [texVomit, 0.95, 2.9, 4.4, 0.2], [texTicket, 0.2, 0.9, 3.05, 0.5], [texCan, 0.24, -4.1, 4.7, 1.1]],
      portrait: [[texBottle, 0.68, -0.75, 3.8, 0.4], [texVomit, 0.85, 0.85, 4.7, 0.2], [texTicket, 0.18, 0.15, 3.05, 0.5], [texCan, 0.22, -0.95, 5.3, 1.1]],
    };
    for (const [key, spread] of [['landscape', 6.5], ['portrait', 1.4]]) {
      for (let i = 0; i < 4; i++) LITTER[key].push([texGum, 0.5, (rng() * 2 - 1) * spread, 3 + rng() * 2.6, rng() * 6]);
      for (let i = 0; i < 7; i++) LITTER[key].push([texButt, 0.05, (rng() * 2 - 1) * spread, 2.95 + rng() * 2.8, rng() * 6]);
    }
    // One name sticker, slapped somewhere between two posters (new spot each visit)
    const STICKER = { gap: Math.random(), x: Math.random(), y: Math.random(), rot: (Math.random() - 0.5) * 0.5 };
    const GRAFFITI = [   // gap i = wall between poster i and i+1; odd gaps are free (even gaps hold the RENKA signs)
      { gap: -5, text: ['Get climbing!'], color: '#52e07a', y: 1.6, rot: 0.07 },
      { gap: -3, mascot: true, y: 1.95, rot: 0.1 },
      { gap: -1, text: ['Grippy!!'], color: '#ff2e88', y: 1.25, rot: -0.08 },
      { gap: 1, text: ['Get climbing!'], color: '#52e07a', y: 1.75, rot: 0.06 },
      { gap: 3, mascot: true, y: 1.95, rot: -0.08 },
      { gap: 5, text: ['Are you even', 'TRAIN-ing?'], color: '#ffb21e', y: 1.6, rot: -0.05 },
    ];
    const texU = canvasTex(30, 30, drawU);
    [texName, texU].forEach((t) => { t.magFilter = THREE.NearestFilter; });

    const M = (opts) => new THREE.MeshLambertMaterial({ flatShading: true, ...opts });
    const box = (w, h, d, mat, x = 0, y = 0, z = 0, parent = scene) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z); parent.add(m); return m;
    };
    const texRepeat = (t, rx, ry) => { const c = t.clone(); c.needsUpdate = true; c.repeat.set(rx, ry); return c; };

    /* ---------------- Station ---------------- */
    const X0 = -110, X1 = 40 + (N - 1) * LAYOUTS.landscape.gap;
    const SPAN = X1 - X0, CX = (X0 + X1) / 2;
    const WALL_TILE = 0.5;        // the supplied wall image is 2x2 tiles = 0.5 m

    // back wall: supplied tiles above, grimy grey render below
    box(SPAN, 4.0, 0.3, M({ map: texRepeat(texWall, SPAN / WALL_TILE, 4.0 / WALL_TILE) }), CX, 2.8, WALL_Z - 0.15);
    box(SPAN, 2.2, 0.3, M({ map: texRepeat(texGrime, SPAN / 2.4, 1) }), CX, -0.3, WALL_Z - 0.12);
    // track bed, rails, third rail, sleepers
    box(SPAN, 0.3, EDGE_Z - WALL_Z, M({ map: texRepeat(texGravel, SPAN / 1.5, 3) }), CX, -1.35, (EDGE_Z + WALL_Z) / 2);
    const steel = M({ color: 0x8d9196 });
    [-0.72, 0.72].forEach((dz) => box(SPAN, 0.12, 0.08, steel, CX, -1.1, TRACK_Z + dz));
    box(SPAN, 0.1, 0.12, M({ color: 0x5b4a38 }), CX, -1.05, TRACK_Z - 1.45);
    {
      const count = Math.floor(SPAN / 0.75);
      const sleepers = new THREE.InstancedMesh(new THREE.BoxGeometry(0.22, 0.1, 2.3), M({ color: 0x3a332d }), count);
      const m4 = new THREE.Matrix4();
      for (let i = 0; i < count; i++) { m4.makeTranslation(X0 + i * 0.75, -1.18, TRACK_Z); sleepers.setMatrixAt(i, m4); }
      scene.add(sleepers);
    }
    // platform: supplied ground texture, supplied edge strip (black/white blocks at the very edge)
    const PLAT_D = 12, EDGE_W = 0.9;
    box(SPAN, 1.2, PLAT_D, M({ map: texRepeat(texGround, SPAN / 1.2, PLAT_D / 1.2) }), CX, -0.6, EDGE_Z + PLAT_D / 2);
    {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(SPAN, EDGE_W), M({ map: texRepeat(texEdge, SPAN / 1.0, 1) }));
      strip.rotation.x = -Math.PI / 2; strip.position.set(CX, 0.008, EDGE_Z + EDGE_W / 2); scene.add(strip);
    }
    // coffered ceiling, baked into one flat plane: a 32 px pixel tile per 1.75 m coffer
    // (beam faces, bevelled sides, a step and the dark recess), lit like everything else
    const CELL = 1.75, SLAB_Y = 4.8, CZ0 = WALL_Z - 0.05, CZ1 = EDGE_Z + PLAT_D;
    const CEIL_D = CZ1 - CZ0, CEIL_Z = (CZ0 + CZ1) / 2;
    {
      const T = 32;
      const tile = canvasTex(T, T, (g) => {
        const px = (x, y, c) => { g.fillStyle = c; g.fillRect(x, y, 1, 1); };
        for (let y = 0; y < T; y++) for (let x = 0; x < T; x++) {
          const e = Math.min(x, y, T - 1 - x, T - 1 - y);            // distance to the tile edge = to the beam line
          const n = (Math.random() - 0.5) * 10;
          let v;
          if (e === 0) v = 58;                                         // seam between beams
          else if (e <= 4) v = 86;                                     // beam underside
          else if (e <= 7) v = (y < x && y < T - 1 - x) || (x < y && x < T - 1 - y) ? 70 : 46;   // beam sides: lit top/left, shaded bottom/right
          else if (e <= 10) v = 52;                                    // step
          else v = 26 + (e - 10) * 0.6;                                // recess, faintly lighter towards the middle
          v = Math.max(0, v + n);
          px(x, y, `rgb(${v | 0},${v + 2 | 0},${v + 5 | 0})`);
        }
      });
      tile.magFilter = THREE.NearestFilter;
      tile.repeat.set(SPAN / CELL, CEIL_D / CELL);
      const frac = (v) => v - Math.floor(v);
      tile.offset.set(0.5 - frac((-60 - X0) / CELL), 0.5 - frac((-1.6 - CZ0) / CELL));   // coffer centres line up with the tubes
      const ceil = new THREE.Mesh(new THREE.PlaneGeometry(SPAN, CEIL_D), M({ map: tile }));
      ceil.rotation.x = Math.PI / 2; ceil.position.set(CX, SLAB_Y, CEIL_Z); scene.add(ceil);
    }
    // platform pillars
    const pillarMat = M({ color: 0x3f5566 });
    for (let x = -36; x < X1; x += 9) box(0.45, SLAB_Y, 0.45, pillarMat, x + 4.5, SLAB_Y / 2, 5.2);
    // station name signs + U signs
    const nameMat = new THREE.MeshBasicMaterial({ map: texName, transparent: true });
    const uMat = new THREE.MeshBasicMaterial({ map: texU, transparent: true });
    for (let x = -30; x < X1; x += 18) {
      const u = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.6), uMat); u.position.set(x, 4.25, EDGE_Z + 2.5); scene.add(u);
      box(0.03, 0.3, 0.03, steel, x, 4.7, EDGE_Z + 2.5);
    }

    /* ---------------- Lights + fluorescent tubes ---------------- */
    scene.add(new THREE.HemisphereLight(0xfff1e0, 0x2a2622, 0.9));
    const tubeOn = new THREE.MeshBasicMaterial({ color: 0xeaf6ff });
    const tubes = [];
    // Tubes sit centred in their coffer, flush under the ceiling
    const housingMat = M({ color: 0x55585c });
    function addTube(x, z) {
      const mesh = box(1.3, 0.08, 0.12, tubeOn, x, 4.72, z);
      box(1.38, 0.05, 0.22, housingMat, x, 4.77, z);
      tubes.push({ mesh, mat: tubeOn, level: 1, next: 0, burst: 0, poster: -1 });
    }
    const TUBE_ZS = [-1.6, -1.6 + 3 * CELL];   // coffer centres over the track side and over the platform
    for (let x = -60; x < X1; x += 2 * CELL) { addTube(x, TUBE_ZS[1]); addTube(x + CELL, TUBE_ZS[0]); }

    function addLight(x, y, z, i = 9, d = 11) { const l = new THREE.PointLight(0xe6f2ff, i, d, 1.6); l.position.set(x, y, z); scene.add(l); return l; }
    const stationLights = [addLight(-6, 4.2, TUBE_ZS[1]), addLight(6, 4.2, TUBE_ZS[1])]; addLight(0, 4.2, -0.5, 7, 12);

    function badTubeNear(x, z) {
      let best = null, bd = 1e9;
      for (const t of tubes) { const d = Math.abs(t.mesh.position.x - x) + Math.abs(t.mesh.position.z - z) * 3; if (d < bd) { bd = d; best = t; } }
      if (best.mat === tubeOn) { best.mat = tubeOn.clone(); best.mesh.material = best.mat; }
      return best;
    }

    /* ---------------- Posters (3D frames + low-res stand-in texture) ---------------- */
    const posterGroup = new THREE.Group(); scene.add(posterGroup);
    const posterLights = [];
    // Stand-in seen before the punch-in: traced from the poster's real DOM layout as colour blocks
    // (backgrounds, divider lines, images, and one bar per line of text), so zooming in lands on the same page.
    const isPainted = (c) => c && c !== 'transparent' && !/,\s*0\)$/.test(c);
    // a cross-origin image without CORS would taint the canvas and break the WebGL upload, so it is skipped
    const drawable = (img) => { try { return new URL(img.currentSrc || img.src, location.href).origin === location.origin || !!img.crossOrigin; } catch (e) { return false; } };
    function posterTexture(el) {
      const W = mode === 'portrait' ? 120 : 220, H = Math.round(W * L.h / L.w);
      const pr = el.getBoundingClientRect(), s = pr.width / el.offsetWidth || 1, k = W / el.offsetWidth;
      const box = (r) => [(r.left - pr.left) / s * k, (r.top - pr.top) / s * k, r.width / s * k, r.height / s * k];
      const range = document.createRange();
      return canvasTex(W, H, (g) => {
        g.fillStyle = getComputedStyle(el).backgroundColor; g.fillRect(0, 0, W, H);
        const pat = g.createPattern(grime, 'repeat'); pat.setTransform(new DOMMatrix().scale(GRIME_PX * k));
        g.save(); g.globalCompositeOperation = 'soft-light'; g.globalAlpha = 0.35; g.fillStyle = pat; g.fillRect(0, 0, W, H); g.restore();
        g.drawImage(EDGE[mode], 0, 0, W, H);
        el.querySelectorAll('*').forEach((n) => {
          const cs = getComputedStyle(n);
          if (cs.display === 'none' || cs.visibility === 'hidden') return;
          g.save();
          const clip = n.parentElement && n.parentElement.closest('.viewport');   // carousel slides outside the frame stay hidden
          if (clip) { g.beginPath(); g.rect(...box(clip.getBoundingClientRect())); g.clip(); }
          const r = n.getBoundingClientRect(), [x, y, w, h] = box(r);
          if (isPainted(cs.backgroundColor)) { g.fillStyle = cs.backgroundColor; g.fillRect(x, y, w, h); }
          const bt = parseFloat(cs.borderTopWidth);
          if (bt >= 2 && isPainted(cs.borderTopColor) && cs.borderTopStyle !== 'none') { g.fillStyle = cs.borderTopColor; g.fillRect(x, y, w, Math.max(1, bt * k)); }
          if (n.tagName === 'IMG' && n.complete && drawable(n)) { try { g.drawImage(n, x, y, w, h); } catch (e) {} }
          const color = n.dataset.color || cs.color;
          if (isPainted(color)) {
            g.fillStyle = color;
            n.childNodes.forEach((t) => {
              if (t.nodeType !== 3 || !t.textContent.trim()) return;
              range.selectNodeContents(t);
              for (const lr of range.getClientRects()) { const [lx, ly, lw, lh] = box(lr); g.fillRect(lx, ly + lh * 0.22, lw, lh * 0.56); }
            });
          }
          g.restore();
        });
      });
    }
    function buildPosters() {
      posterGroup.traverse((o) => { if (o.material && o.material.map && o.material.map !== texName && !KEEP.has(o.material.map)) o.material.map.dispose(); });
      posterGroup.clear();
      posterLights.forEach((l) => scene.remove(l)); posterLights.length = 0;
      const FRAME = 0.12;   // visible frame border in metres
      const barH = M({ map: texRepeat(texFrameH, (L.w + 2 * FRAME) / 0.8, 1) });
      const barV = M({ map: texRepeat(texFrameV, 1, L.h / 0.8) });
      posterEls.forEach((el, i) => {
        const x = posterX(i);
        const fz = WALL_Z + 0.02;
        box(L.w + 2 * FRAME, FRAME, 0.08, barH, x, POSTER_Y + (L.h + FRAME) / 2, fz, posterGroup);
        box(L.w + 2 * FRAME, FRAME, 0.08, barH, x, POSTER_Y - (L.h + FRAME) / 2, fz, posterGroup);
        box(FRAME, L.h, 0.08, barV, x - (L.w + FRAME) / 2, POSTER_Y, fz, posterGroup);
        box(FRAME, L.h, 0.08, barV, x + (L.w + FRAME) / 2, POSTER_Y, fz, posterGroup);
        const face = new THREE.Mesh(new THREE.PlaneGeometry(L.w, L.h), new THREE.MeshBasicMaterial({ map: posterTexture(el) }));
        face.position.set(x, POSTER_Y, WALL_Z + 0.065); posterGroup.add(face);
        const l = new THREE.PointLight(0xe6f2ff, 6, 7, 1.6); l.position.set(x, 4.3, -1.4); scene.add(l); posterLights.push(l);
      });
      // RENKA name signs in every other gap, at poster height (extends left of the first poster for the station view)
      const signW = mode === 'portrait' ? 1.5 : 1.9;
      for (let i = -8; i < N + 2; i += 2) {
        const sign = new THREE.Mesh(new THREE.PlaneGeometry(signW, signW * 34 / 128), nameMat);
        sign.position.set((posterX(i) + posterX(i + 1)) / 2, POSTER_Y + L.h * 0.22, WALL_Z + 0.02); posterGroup.add(sign);
      }
      const freeW = (L.gap - L.w - 2 * FRAME) * 0.88;
      for (const gf of GRAFFITI) {
        const map = gf.mascot ? texMascot : graffitiTex(gf.text, gf.color);
        const aspect = map.image.width / map.image.height;
        let h = gf.mascot ? 1.3 : 0.46 * gf.text.length, w = h * aspect;
        if (w > freeW) { h *= freeW / w; w = freeW; }
        const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), M(gf.mascot ? { map, transparent: true, depthWrite: false } : { map, transparent: true, alphaTest: 0.5 }));
        m.position.set((posterX(gf.gap) + posterX(gf.gap + 1)) / 2, gf.y, WALL_Z + 0.015); m.rotation.z = gf.rot || 0;
        posterGroup.add(m);
      }
      // name sticker: odd gaps hold graffiti low down, so it goes above that; even gaps hold the sign high, so it goes low
      {
        const gap = Math.floor(STICKER.gap * (N - 1)), sw = 0.42, sh = sw * imgSticker.height / imgSticker.width;
        const st = new THREE.Mesh(new THREE.PlaneGeometry(sw, sh), M({ map: texSticker, transparent: true, alphaTest: 0.5 }));
        const y = gap % 2 ? 2.3 + STICKER.y * 0.2 : 0.95 + STICKER.y * 0.8;
        st.position.set((posterX(gap) + posterX(gap + 1)) / 2 + (STICKER.x - 0.5) * freeW * 0.7, y, WALL_Z + 0.018);
        st.rotation.z = STICKER.rot; posterGroup.add(st);
      }
      // pictograms on small blue plates, high in the free wall between the posters
      const pictoW = mode === 'portrait' ? 0.5 : 0.62;
      for (const [gap, name] of PICTO_SLOTS) {
        const m = new THREE.Mesh(new THREE.PlaneGeometry(pictoW, pictoW), M({ map: PICTOS[name] }));
        m.position.set((posterX(gap) + posterX(gap + 1)) / 2, 3.25, WALL_Z + 0.02); posterGroup.add(m);
      }
      // the tag on the platform floor in front of the train, centred in the landing view
      // stretched along the floor (like street-art lettering) so it reads undistorted from the landing camera
      if (floorTag) {
        const F = mode === 'portrait' ? { w: 1.8, stretch: 2.4, z: 4.1 } : { w: 2.6, stretch: 2.1, z: 4.25 };
        floorTag.scale.set(F.w, F.w * tagImg.height / tagImg.width * F.stretch, 1);
        floorTag.position.set(0, 0.01, F.z);
      }
      // litter on the platform floor
      for (const [map, size, x, z, rot] of LITTER[mode]) {
        const m = new THREE.Mesh(new THREE.PlaneGeometry(size * map.image.width / map.image.height, size),
          M({ map, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }));
        m.rotation.set(-Math.PI / 2, 0, rot); m.position.set(x, 0.012, z); posterGroup.add(m);
      }
      // the RENKA piece sprayed high on the wall: top left of the landing view, above the waiting train
      {
        const h = mode === 'portrait' ? 1.05 : 1.5, w = h * pieceImg.width / pieceImg.height;
        const tag = new THREE.Mesh(new THREE.PlaneGeometry(w, h), M({ map: texRenkaWall, transparent: true, alphaTest: 0.5 }));
        tag.position.set(mode === 'portrait' ? -1.7 : -5.4, mode === 'portrait' ? 4.2 : 4.1, WALL_Z + 0.015); tag.rotation.z = 0.04;
        posterGroup.add(tag);
      }
      // red arrows sprayed from the RENKA piece down to the first billboard: [x, y, size m]
      {
        const ARROWS = mode === 'portrait'
          ? [[-0.25, 4.3, 0.72], [0.75, 4.15, 0.72]]
          : [[-3.45, 4.3, 0.95], [-2.15, 4.0, 0.95], [-0.85, 4.28, 0.85]];
        const x0 = posterX(0), top = POSTER_Y + L.h / 2;
        ARROWS.forEach(([x, y, size], i) => {
          const tx = Math.min(Math.max(x * 0.55, x0 - L.w / 2 + 0.4), x0 + L.w / 2 - 0.4);
          const m = new THREE.Mesh(new THREE.PlaneGeometry(size, size),
            M({ map: sprayArrow(Math.atan2(top - 0.4 - y, tx - x), 7 + i * 13), transparent: true, alphaTest: 0.5 }));
          m.position.set(x, y, WALL_Z + 0.016); posterGroup.add(m);
        });
      }
    }

    let flickerTubes = [];
    function assignFlicker() {
      flickerTubes.forEach((t) => { t.mat.color.setScalar(1); if (t.light) t.light.intensity = 9; });
      posterEls.forEach((el) => el.style.setProperty('--dim', '0'));
      // [x, z, poster it dims, station light it drags along]
      flickerTubes = [
        [posterX(1) - 0.6, -1.6, 1, null], [posterX(2) + 0.5, -1.6, 2, null], [posterX(5) + 0.4, -1.6, 5, null],
        [(posterX(3) + posterX(4)) / 2, -1.6, -1, null], [-13, -1.6, -1, null],
        [-5.5, TUBE_ZS[1], -1, stationLights[0]], [6.5, TUBE_ZS[1], -1, stationLights[1]],
      ].map(([x, z, poster, light]) => Object.assign(badTubeNear(x, z), { poster, light, next: performance.now() + Math.random() * 5000 }));
    }

    /* ---------------- Train (3 cars, grimy Berlin yellow) ---------------- */
    // Graffiti tag on the platform floor in front of the train (the cars stay plain): the artist's drawing keyed out
    // of its white paper, sprayed in saffron with a dark outline so it reads on the dark floor.
    const pieceTex = (src) => { const t = new THREE.CanvasTexture(src); t.colorSpace = THREE.SRGBColorSpace; t.magFilter = THREE.NearestFilter; return t; };
    const TAG_K = 2;   // canvas pixels per drawing pixel: room for the 2 px outline around the chunky tag
    const fullPiece = document.createElement('canvas'); fullPiece.width = tagImg.width * TAG_K; fullPiece.height = tagImg.height * TAG_K;
    {
      const g = fullPiece.getContext('2d'), W = fullPiece.width, H = fullPiece.height;
      const src = document.createElement('canvas'); src.width = tagImg.width; src.height = tagImg.height;
      const sg = src.getContext('2d'); sg.drawImage(tagImg, 0, 0);
      const d = sg.getImageData(0, 0, src.width, src.height), a = d.data;
      for (let i = 0; i < a.length; i += 4) {
        const ink = Math.min(255 - Math.min(a[i], a[i + 1], a[i + 2]), a[i + 3]);   // dark and opaque = paint
        a[i] = 234; a[i + 1] = 196; a[i + 2] = 53; a[i + 3] = ink > 110 ? 255 : 0;
      }
      sg.putImageData(d, 0, 0);
      const halo = document.createElement('canvas'); halo.width = W; halo.height = H;
      const hg = halo.getContext('2d'); hg.imageSmoothingEnabled = false;
      for (const [ox, oy] of [[-2, 0], [2, 0], [0, -2], [0, 2], [-2, -2], [2, -2], [-2, 2], [2, 2]]) hg.drawImage(src, ox, oy, W, H);
      hg.globalCompositeOperation = 'source-in'; hg.fillStyle = 'rgb(20,22,25)'; hg.fillRect(0, 0, W, H);
      g.imageSmoothingEnabled = false; g.drawImage(halo, 0, 0); g.drawImage(src, 0, 0, W, H);
      hardAlpha(g, W, H);
    }
    {
      const t = pieceTex(fullPiece); KEEP.add(t);
      floorTag = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), M({ map: t, transparent: true, alphaTest: 0.5, polygonOffset: true, polygonOffsetFactor: -1 }));
      floorTag.rotation.set(-Math.PI / 2, 0, 0.05); scene.add(floorTag);
    }
    // RENKA logo as a pixel piece on the wall panels between the doors (under the middle windows), where no door
    // leaf ever slides over it: white paper keyed out, colours snapped to the logo's red and blue, dark outline.
    const logoImg = await loadImg(D.texLogo);
    let logoMat = null;
    if (logoImg.width > 8) {   // a failed load gives the 2 px stand-in
      const lw = 88, lh = Math.round(lw * logoImg.height / logoImg.width), P = 2;
      const lo = document.createElement('canvas'); lo.width = lw; lo.height = lh;
      const lg = lo.getContext('2d'); lg.drawImage(logoImg, 0, 0, lw, lh);
      const d = lg.getImageData(0, 0, lw, lh), a = d.data;
      for (let i = 0; i < a.length; i += 4) {
        const [r, g, b] = [a[i], a[i + 1], a[i + 2]];
        if ((r + g + b) / 3 > 205 || a[i + 3] < 110) { a[i + 3] = 0; continue; }
        const blue = b > r;
        a[i] = blue ? 74 : 204; a[i + 1] = blue ? 86 : 22; a[i + 2] = blue ? 238 : 78; a[i + 3] = 255;
      }
      lg.putImageData(d, 0, 0);
      const c = document.createElement('canvas'); c.width = lw + 2 * P; c.height = lh + 2 * P;
      const g = c.getContext('2d'); g.imageSmoothingEnabled = false;
      for (const [ox, oy] of [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]]) g.drawImage(lo, P + ox, P + oy);
      g.globalCompositeOperation = 'source-in'; g.fillStyle = 'rgb(20,22,25)'; g.fillRect(0, 0, c.width, c.height);
      g.globalCompositeOperation = 'source-over'; g.drawImage(lo, P, P);
      const t = pieceTex(c); KEEP.add(t);
      logoMat = M({ map: t, transparent: true, alphaTest: 0.5 });
    }
    const LOGO_W = 1.6;   // the panel between two door openings is 1.79 m wide once the leaves are open
    const train = new THREE.Group(); scene.add(train);
    const CAR = 15.5, GAP = 0.5, CARS = 3, TRAIN_LEN = CARS * CAR + (CARS - 1) * GAP;
    const yellow = M({ map: texRepeat(texTrain, 4, 1) }), roofMat = M({ color: 0x8f8a6a }), glass = M({ map: texGlass }), under = M({ color: 0x1c1c1c });
    const doorMat = M({ map: texTrainDoor }), doorGap = M({ color: 0x0c0c0c }), lamp = new THREE.MeshBasicMaterial({ color: 0xfff6d0 }), tail = new THREE.MeshBasicMaterial({ color: 0xff3020 });
    const doorLeaves = [];
    for (let c = 0; c < CARS; c++) {
      const cx = -TRAIN_LEN / 2 + CAR / 2 + c * (CAR + GAP);
      const car = new THREE.Group(); car.position.x = cx; train.add(car);
      box(CAR, 3.0, 2.5, yellow, 0, 0.85, TRACK_Z, car);
      box(CAR - 0.3, 0.18, 2.3, roofMat, 0, 2.44, TRACK_Z, car);
      box(CAR - 0.6, 0.35, 2.0, under, 0, -0.85, TRACK_Z, car);
      for (const w of [-6.6, -2.175, 2.175, 6.6]) box(1.5, 0.85, 0.02, glass, w, 1.55, TRACK_Z + 1.26, car);  // clear of the open door leaves
      if (logoMat) {
        for (const [w, rot] of [[-2.175, 0.04], [2.175, -0.03]]) {
          const tag = new THREE.Mesh(new THREE.PlaneGeometry(LOGO_W, LOGO_W * logoMat.map.image.height / logoMat.map.image.width), logoMat);
          tag.position.set(w, 0.62, TRACK_Z + 1.27); tag.rotation.z = rot; car.add(tag);
        }
      }
      for (const dx of [-4.35, 0, 4.35]) {
        box(1.3, 2.2, 0.02, doorGap, dx, 0.75, TRACK_Z + 1.255, car);
        for (const side of [-1, 1]) {
          const leaf = box(0.62, 2.2, 0.04, doorMat, dx + side * 0.95, 0.75, TRACK_Z + 1.28, car);
          box(0.4, 0.7, 0.01, glass, 0, 0.45, 0.025, leaf);
          leaf.userData = { closed: dx + side * 0.32, open: dx + side * 0.95 };
          doorLeaves.push(leaf);
        }
      }
    }
    box(0.04, 0.9, 1.9, glass, -TRAIN_LEN / 2 - 0.01, 1.55, TRACK_Z);
    [-0.75, 0.75].forEach((dz) => { box(0.04, 0.16, 0.3, lamp, -TRAIN_LEN / 2 - 0.02, 0.15, TRACK_Z + dz); box(0.04, 0.14, 0.22, tail, TRAIN_LEN / 2 + 0.02, 0.15, TRACK_Z + dz); });
    const cabLight = new THREE.PointLight(0xfff0c0, 0, 14, 1.4); cabLight.position.set(-TRAIN_LEN / 2 - 1, 0.6, TRACK_Z + 1.5); train.add(cabLight);

    /* ---------------- Spinning ledge (the WebP from the theme; low-poly stand-in only if it is missing) ---------------- */
    const spinEl = $('.spin');
    let spin = null;
    if (spinEl && !spinEl.querySelector('img')) {
      const r = new THREE.WebGLRenderer({ antialias: false, alpha: true });
      r.setPixelRatio(1); r.setSize(72, 72, false); r.setClearColor(0x000000, 0);
      spinEl.appendChild(r.domElement);
      const s = new THREE.Scene();
      const cam = new THREE.PerspectiveCamera(32, 1, 0.1, 20); cam.position.set(0, 0.9, 4.2); cam.lookAt(0, 0.05, 0);
      s.add(new THREE.AmbientLight(0xffffff, 1.1));
      const dl = new THREE.DirectionalLight(0xfff3dc, 2.2); dl.position.set(2, 3, 2); s.add(dl);
      const wood = M({ color: 0xd2a066 }), woodDark = M({ color: 0xa8763f }), cord = M({ color: 0xd8342c });
      const ledge = new THREE.Group(); s.add(ledge);
      box(1.6, 0.42, 0.34, wood, 0, 0.0, -0.06, ledge);           // body
      box(1.6, 0.18, 0.2, woodDark, 0, -0.12, 0.2, ledge);         // the grip edge
      const loop = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.035, 4, 10, Math.PI), cord);
      loop.position.set(0, 0.2, -0.06); ledge.add(loop);
      [-0.42, 0.42].forEach((x) => box(0.07, 0.3, 0.07, cord, x, 0.1, -0.06, ledge));
      ledge.rotation.x = 0.15;
      spin = { r, s, cam, ledge };
    }

    /* ---------------- Low-res headline ---------------- */
    const LOFI_K = 1 / 3.2;            // one canvas pixel = 3.2 poster px
    function lofi(el) {
      el.style.color = ''; el.style.backgroundImage = '';
      if (el.hasAttribute('data-crisp')) return;   // hero headline stays real Barlow Condensed, not pixelated
      const cs = getComputedStyle(el);
      el.dataset.color = cs.color;      // the poster texture reads this once the text goes transparent
      const fs = parseFloat(cs.fontSize), k = LOFI_K;
      const lh = parseFloat(cs.lineHeight) || fs * 0.9;
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(el.clientWidth * k)); c.height = Math.max(1, Math.round(el.clientHeight * k));
      const g = c.getContext('2d');
      g.font = `${cs.fontWeight} ${fs * k}px ${cs.fontFamily}`; g.fillStyle = cs.color; g.textBaseline = 'middle';
      el.innerText.split('\n').forEach((line, i) => g.fillText(line, 0, (i + 0.5) * lh * k));
      const d = g.getImageData(0, 0, c.width, c.height);   // drop antialiasing: every pixel fully on or off
      for (let i = 3; i < d.data.length; i += 4) d.data[i] = d.data[i] > 110 ? 255 : 0;
      g.putImageData(d, 0, 0);
      el.style.backgroundImage = `url(${c.toDataURL()})`; el.style.color = 'transparent';
    }
    // Pixel headlines never wrap (lines come only from <br>), so size them to the poster:
    // the portrait hero headline spans 86% of the width, any other one shrinks only if it would not fit.
    function fitLofi(h) {
      h.style.fontSize = '';
      const heroPortrait = mode === 'portrait' && h.closest('.p-hero');
      const cs = getComputedStyle(h), fs = parseFloat(cs.fontSize);
      const g = document.createElement('canvas').getContext('2d');
      g.font = `${cs.fontWeight} ${fs}px ${cs.fontFamily}`;
      const widest = Math.max(...h.innerText.split('\n').map((l) => g.measureText(l).width));
      const pcs = getComputedStyle(h.parentElement);
      const room = (h.parentElement.clientWidth - parseFloat(pcs.paddingLeft) - parseFloat(pcs.paddingRight)) * (heroPortrait ? 0.86 : 1);
      if (heroPortrait || widest > room) h.style.fontSize = Math.floor(fs * room / widest) + 'px';
    }
    const lofiAll = () => $$('.lofi').forEach((el) => { fitLofi(el); lofi(el); });

    /* ---------------- Tester bill placement (in shop-poster px) ---------------- */
    const TESTER_BOX = { landscape: { l: 470, t: -26, r: 3.5 }, portrait: { l: -22, t: 192, r: -3 } };
    let testerPlaced = false;
    function placeTester(x, y, s, hidden) {
      if (!testerEl) return;
      const b = TESTER_BOX[mode];
      testerEl.style.visibility = hidden ? 'hidden' : 'visible';
      if (!hidden) testerEl.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${s.toFixed(4)}) translate(${b.l}px, ${b.t}px) rotate(${b.r}deg)`;
    }

    /* ---------------- Camera fit for poster i ---------------- */
    function railPose(railPos, out = { pos: new THREE.Vector3(), target: new THREE.Vector3() }) {
      const aspect = innerWidth / innerHeight;
      const tanH = Math.tan(THREE.MathUtils.degToRad(L.fov / 2));
      const d = Math.max(L.h / L.fitH, L.w / (L.fitW * aspect)) / (2 * tanH);
      const visH = 2 * d * tanH;
      const x = railPos * L.gap, y = POSTER_Y - visH * L.lift;
      out.pos.set(x, y, WALL_Z + d); out.target.set(x, y, WALL_Z);
      return out;
    }

    /* ---------------- State ---------------- */
    let phase = 'station';           // station → transit → rail → transit (zoom out, the train pulls in again) → station
    let railPos = 0, railTarget = 0, anchor = 0, snapTimer = 0;
    const board = $('.rs-board'), boardMsg = $('.rs-board-msg');
    const hint = $('.rs-hint');
    const arrow = $('.rs-arrow'), shaft = arrow.querySelector('.shaft'), head = arrow.querySelector('.head');
    shopInView = () => (active ? phase === 'rail' && Math.abs(railPos - SHOP) < 0.5 : shopVisible);

    boardMsg.textContent = I.board;
    buildPosters(); lofiAll(); renderTester();
    assignFlicker();

    function depart() {
      if (phase !== 'station') return;
      phase = 'transit';
      hint.classList.add('out');
      boardMsg.textContent = I.board_go; boardMsg.classList.add('blink');
      const k = reduceMotion ? 0.35 : 1;
      const rail = railPose(0);
      const tl = gsap.timeline({
        onComplete: () => { train.visible = false; railDone(); },
      });
      doorLeaves.forEach((leaf) => tl.to(leaf.position, { x: leaf.userData.closed, duration: 0.45 * k, ease: 'power2.inOut' }, 0.2 * k));
      tl.to(cabLight, { intensity: 8, duration: 0.3 }, 0.6 * k);
      tl.to(train.position, { x: -125, duration: 3.6 * k, ease: 'power2.in' }, 0.9 * k);
      tl.to(train.position, { y: 0.03, duration: 0.12, repeat: 14, yoyo: true, ease: 'sine.inOut' }, 1.4 * k);
      tl.to(camPos, { x: rail.pos.x, y: rail.pos.y, z: rail.pos.z, duration: 1.9 * k, ease: 'power3.inOut' }, 2.7 * k);
      tl.to(camTarget, { x: rail.target.x, y: rail.target.y, z: rail.target.z, duration: 1.9 * k, ease: 'power3.inOut' }, 2.7 * k);
      tl.call(() => postersLayer.classList.add('on'), null, 4.0 * k);
    }

    function railDone() {
      phase = 'rail'; railPos = railTarget = anchor = 0;
      board.classList.add('out'); arrow.classList.add('on');
    }
    // back from the first poster: zoom out to the station while the train pulls in again from the right,
    // stops and opens its doors; then it waits like on arrival and the next scroll sends it off again
    function zoomOut() {
      if (phase !== 'rail') return;
      phase = 'transit'; railTarget = anchor = 0;
      postersLayer.classList.remove('on'); arrow.classList.remove('on');
      boardMsg.textContent = I.board_go; boardMsg.classList.add('blink'); board.classList.remove('out');
      gsap.killTweensOf(train.position);
      train.position.set(125, 0, train.position.z); train.visible = true; cabLight.intensity = 8;
      doorLeaves.forEach((leaf) => { leaf.position.x = leaf.userData.closed; });
      const k = reduceMotion ? 0.35 : 1;
      gsap.timeline({
        onComplete: () => {
          phase = 'station';
          boardMsg.textContent = I.board; boardMsg.classList.remove('blink'); hint.classList.remove('out');
        },
      })
        .to(camPos, { x: STATION.pos.x, y: STATION.pos.y, z: STATION.pos.z, duration: 1.7 * k, ease: 'power3.inOut' }, 0.2 * k)
        .to(camTarget, { x: STATION.target.x, y: STATION.target.y, z: STATION.target.z, duration: 1.7 * k, ease: 'power3.inOut' }, 0.2 * k)
        .to(train.position, { x: 0, duration: 3.4 * k, ease: 'power2.out' }, 0.5 * k)
        .to(cabLight, { intensity: 0, duration: 0.6 }, 3.6 * k)
        .add(doorLeaves.map((leaf) => gsap.to(leaf.position, { x: leaf.userData.open, duration: 0.45 * k, ease: 'power2.inOut' })), 4.1 * k);
    }
    const start = () => depart();
    // arriving with a poster in the URL (language switch, returning from a form post): skip the train
    function arriveAt(i) {
      phase = 'rail'; railPos = railTarget = anchor = i;
      train.visible = false;
      const p = railPose(i); camPos.copy(p.pos); camTarget.copy(p.target);
      hint.classList.add('out'); board.classList.add('out'); arrow.classList.add('on'); postersLayer.classList.add('on');
    }

    function goTo(i) {
      if (phase !== 'rail') return;
      railTarget = anchor = clampT(i, 0, N - 1);
    }
    function snap() {
      const delta = railTarget - anchor;
      if (anchor === 0 && delta < -0.12) { zoomOut(); return; }
      let to = anchor;
      if (delta > 0.12) to = Math.max(anchor + 1, Math.round(railTarget));
      else if (delta < -0.12) to = Math.min(anchor - 1, Math.round(railTarget));
      goTo(to);
    }

    /* ---------------- Input (only events inside the station; Horizon's cart drawer keeps its own) ---------------- */
    const sheet = $('.rs-sheet');
    const inSheet = (e) => e.target.closest && e.target.closest('.rs-sheet');
    const ours = (e) => active && e.target instanceof Node && (root.contains(e.target) || e.target === document.body || e.target === document.documentElement);
    addEventListener('wheel', (e) => {
      if (!ours(e) || inSheet(e)) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
      const d = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * unit;
      if (phase === 'station') { if (d > 4) start(); return; }
      if (phase !== 'rail') return;
      railTarget = clampT(railTarget + d / 520, -0.2, N - 1 + 0.2);
      clearTimeout(snapTimer); snapTimer = setTimeout(snap, 140);
    }, { passive: false });

    // Touch: swipe up/left = forward. Inside the carousel a sideways swipe changes the photo instead.
    let touch = null;
    addEventListener('touchstart', (e) => {
      if (!ours(e) || inSheet(e) || e.touches.length !== 1 || e.target.closest('.rs-arrow')) return;
      const t = e.touches[0];
      touch = { x: t.clientX, y: t.clientY, start: railTarget, moved: false, carousel: !!e.target.closest('.carousel'), axis: null };
    }, { passive: true });
    addEventListener('touchmove', (e) => {
      if (!touch || inSheet(e)) return;
      e.preventDefault();
      const t = e.touches[0];
      const dx = touch.x - t.clientX, dy = touch.y - t.clientY;
      if (!touch.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (touch.axis) touch.moved = true;
      touch.dx = dx;
      if (touch.carousel && touch.axis === 'x') return;
      const d = touch.axis === 'x' ? dx : dy;
      if (phase === 'station') { if (d > 24) start(); return; }
      if (phase !== 'rail' || !touch.axis) return;
      railTarget = clampT(touch.start + d / (Math.min(innerWidth, innerHeight) * 0.85), -0.25, N - 1 + 0.25);
    }, { passive: false });
    addEventListener('touchend', () => {
      if (!touch) return;
      if (touch.carousel && touch.axis === 'x') { if (Math.abs(touch.dx) > 30) showSlide(slide + Math.sign(touch.dx)); }
      else if (phase === 'rail' && touch.moved) snap();
      touch = null;
    });

    addEventListener('keydown', (e) => {
      if (!ours(e)) return;
      if (!sheet.hidden) { if (e.key === 'Escape') closeSheet(); return; }
      if (e.target.matches && e.target.matches('input, textarea, select')) return;
      const fwd = ['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(e.key);
      const back = ['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key);
      if ((phase === 'station') && (fwd || e.key === 'Enter')) { e.preventDefault(); start(); return; }
      if (phase !== 'rail') return;
      if (fwd) { e.preventDefault(); goTo(Math.round(railTarget) + 1); }
      else if (back) { e.preventDefault(); Math.round(railTarget) <= 0 ? zoomOut() : goTo(Math.round(railTarget) - 1); }
      else if (e.key === 'Home') goTo(0);
      else if (e.key === 'End') goTo(N - 1);
    });
    hint.addEventListener('click', start);

    postersLayer.addEventListener('click', (e) => {
      if (!active) return;
      const lb = e.target.closest('a[data-lang]');
      if (lb) { lb.href = `${lb.href.split('#')[0]}#rs-p${clampT(Math.round(railTarget), 0, N - 1)}`; return; }   // Shopify switches the language; come back to this poster
      const go = e.target.closest('[data-go]');
      if (go) { e.preventDefault(); const v = go.dataset.go; goTo(v === 'next' ? Math.round(railTarget) + 1 : +v); return; }
      const legal = e.target.closest('[data-legal]');
      if (legal) { e.preventDefault(); openSheet(legal.href, legal.textContent.trim(), legal); }
    });
    postersLayer.addEventListener('focusin', (e) => { const i = e.target.closest('.tester') ? SHOP : posterEls.indexOf(e.target.closest('.poster')); if (i >= 0 && phase === 'rail') goTo(i); });
    // the posters layer is overflow: hidden; anchors and focus must not scroll it
    postersLayer.addEventListener('scroll', () => { postersLayer.scrollTop = 0; postersLayer.scrollLeft = 0; });

    /* ---------------- Legal overlay: fetches the policy or page and shows its main content ---------------- */
    const sheetTitle = sheet.querySelector('#rs-sheet-title'), sheetBody = sheet.querySelector('.body'), sheetClose = sheet.querySelector('.close');
    let sheetOpener = null, sheetReq = 0;
    async function openSheet(url, title, opener) {
      const req = ++sheetReq;
      sheetTitle.textContent = title;
      sheetBody.innerHTML = `<p>${esc(I.legal_loading)}</p>`; sheetBody.scrollTop = 0;
      sheet.hidden = false; sheetOpener = opener; sheetClose.focus();
      try {
        const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (req !== sheetReq) return;
        const main = doc.querySelector('#MainContent') || doc.querySelector('main') || doc.body;
        main.querySelectorAll('script, style, link, noscript, template, iframe, .section-background').forEach((n) => n.remove());
        main.querySelectorAll('*').forEach((n) => { for (const a of [...n.attributes]) if (/^on/i.test(a.name)) n.removeAttribute(a.name); });
        const h1 = main.querySelector('h1');
        if (h1) { sheetTitle.textContent = h1.textContent.trim() || title; h1.remove(); }
        sheetBody.innerHTML = (I.lang !== 'de' ? `<p class="note">${esc(I.legal_lang)}</p>` : '') + main.innerHTML;
        if (sheetBody.querySelector('[data-renka-withdrawal]')) {
          const { wireWithdrawal } = await import(root.dataset.withdrawalJs);
          wireWithdrawal(sheetBody, { inline: true });
        }
      } catch (err) {
        if (req !== sheetReq) return;
        sheetBody.innerHTML = `<p>${esc(I.legal_error)}</p><p><a href="${esc(url)}">${esc(title)}</a></p>`;
      }
    }
    function closeSheet() { sheetReq++; sheet.hidden = true; sheetOpener && sheetOpener.isConnected && sheetOpener.focus({ preventScroll: true }); }
    sheetClose.addEventListener('click', closeSheet);
    sheet.addEventListener('click', (e) => { if (e.target === sheet) closeSheet(); });

    /* ---------------- Draggable progress arrow (mobile) ---------------- */
    // head centre sits at 37 + p * (full - 30) px; each poster gets a stop mark there
    const ticksEl = arrow.querySelector('.ticks');
    ticksEl.innerHTML = posterEls.map(() => '<span class="tick"></span>').join('');
    function placeTicks() {
      const full = arrow.offsetWidth - 14;
      ticksEl.querySelectorAll('.tick').forEach((t, i) => { t.style.left = (37 + (i / (N - 1)) * (full - 30)) + 'px'; });
    }
    function dragArrow(e) {
      const r = arrow.getBoundingClientRect(), full = r.width - 14;
      railTarget = clampT((e.clientX - r.left - 37) / (full - 30), 0, 1) * (N - 1);
    }
    arrow.addEventListener('pointerdown', (e) => {
      if (phase !== 'rail') return;
      arrow.setPointerCapture(e.pointerId); arrow.classList.add('drag'); dragArrow(e);
    });
    arrow.addEventListener('pointermove', (e) => { if (arrow.classList.contains('drag')) dragArrow(e); });
    const endDrag = () => { if (!arrow.classList.contains('drag')) return; arrow.classList.remove('drag'); goTo(Math.round(railTarget)); };
    arrow.addEventListener('pointerup', endDrag);
    arrow.addEventListener('pointercancel', endDrag);

    /* ---------------- Resize ---------------- */
    function resize() {
      if (!active) return;
      const W = innerWidth, H = innerHeight;
      const newMode = W / H < 0.9 ? 'portrait' : 'landscape';
      const scale = Math.min(1, INTERNAL_H / H);
      renderer.setSize(Math.round(W * scale), Math.round(H * scale), false);
      camera.aspect = W / H;
      if (newMode !== mode) {
        mode = newMode; L = LAYOUTS[mode];
        root.classList.toggle('portrait', mode === 'portrait');
        buildPosters(); assignFlicker(); lofiAll();
      }
      camera.fov = L.fov;
      camera.updateProjectionMatrix();
      if (phase === 'rail') { const p = railPose(railPos); camPos.copy(p.pos); camTarget.copy(p.target); }
      placeTicks();
    }
    addEventListener('resize', resize);

    /* ---------------- Per-frame ---------------- */
    const v = new THREE.Vector3();
    function layoutPosters() {
      if (!postersLayer.classList.contains('on')) return;
      const W = innerWidth, H = innerHeight;
      posterEls.forEach((el, i) => {
        const x = posterX(i);
        v.set(x - L.w / 2, POSTER_Y + L.h / 2, WALL_Z + 0.07).project(camera);
        const x1 = (v.x + 1) / 2 * W, y1 = (1 - v.y) / 2 * H;
        v.set(x + L.w / 2, POSTER_Y - L.h / 2, WALL_Z + 0.07).project(camera);
        const x2 = (v.x + 1) / 2 * W;
        const off = x2 < -50 || x1 > W + 50;
        el.style.visibility = off ? 'hidden' : 'visible';
        const s = (x2 - x1) / el.offsetWidth;
        if (i === SHOP) placeTester(x1, y1, s, off);
        if (off) return;
        el.style.transform = `translate3d(${x1.toFixed(2)}px, ${y1.toFixed(2)}px, 0) scale(${s.toFixed(4)})`;
      });
    }

    function flicker(now) {
      if (reduceMotion) return;
      for (const t of flickerTubes) {
        if (now > t.next) {
          if (t.burst > 0) { t.level = t.level > 0.7 ? 0.56 + Math.random() * 0.1 : 1; t.burst--; t.next = now + 40 + Math.random() * 110; }
          else { t.level = 1; t.burst = 2 + (Math.random() * 7 | 0); t.next = now + 2400 + Math.random() * 6400; }
          t.mat.color.setScalar(0.25 + 0.75 * t.level);
          if (t.light) t.light.intensity = 9 * (0.35 + 0.65 * t.level);
          if (t.poster >= 0) {
            posterLights[t.poster] && (posterLights[t.poster].intensity = 6 * t.level);
            posterEls[t.poster].style.setProperty('--dim', ((1 - t.level) * 0.22).toFixed(3));
          }
        }
      }
    }

    let last = performance.now(), spinAcc = 0;
    const pose = { pos: new THREE.Vector3(), target: new THREE.Vector3() };
    function frame(now) {
      if (!active) return;
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      if (phase === 'rail') {
        railPos += (railTarget - railPos) * (1 - Math.exp(-dt * (reduceMotion ? 20 : 7)));
        railPose(railPos, pose); camPos.copy(pose.pos); camTarget.copy(pose.target);
        if (testerEl && !testerPlaced && Math.abs(railPos - SHOP) < 0.2) { testerPlaced = true; testerEl.classList.add('placed'); }
        const p = clampT(railPos / (N - 1), 0, 1);
        const full = arrow.offsetWidth - 14;
        const len = 30 + p * (full - 30);
        shaft.style.width = len + 'px'; head.style.left = len + 'px';
      }
      camera.position.copy(camPos); camera.lookAt(camTarget); camera.updateMatrixWorld();
      flicker(now);
      renderer.render(scene, camera);
      layoutPosters();
      // spinner: stepped at ~12 fps so it reads like a GIF; only while the hero is on screen
      if (spin && postersLayer.classList.contains('on') && railPos < 0.9) {
        spinAcc += dt;
        if (spinAcc > 1 / 12) { spin.ledge.rotation.y += reduceMotion ? 0 : spinAcc * 1.6; spinAcc = 0; spin.r.render(spin.s, spin.cam); }
      }
      requestAnimationFrame(frame);
    }

    /* ---------------- Go ---------------- */
    active = true;
    resize();
    scrollTo(0, 0); postersLayer.scrollTop = 0;
    const m = /^rs-p(\d+)$/.exec(hashId);
    const startAt = m ? clampT(+m[1], 0, N - 1) : hashId === 'renka-signup' ? SHOP : hashId === 'renka-newsletter' ? N - 1 : -1;
    if (startAt >= 0) {
      arriveAt(startAt);
      history.replaceState(history.state, '', location.pathname + location.search);
    } else {
      // nobody sends the train off within 3 s of opening the page: it leaves on its own once everything is loaded
      // (a short moment after loading at the earliest, so the waiting train is seen at all)
      setTimeout(() => { if (active && phase === 'station') depart(); }, Math.max(800, 3000 - performance.now()));
    }
    if (spin) spin.r.render(spin.s, spin.cam);
    requestAnimationFrame(frame);
    root.classList.remove('is-booting');
    root.dataset.station = 'on';

    // redraw text textures once webfonts are in (the canvas fonts are requested explicitly: no DOM text uses them first),
    // and the poster stand-ins once the hero photo is decoded, so the far-away hero shows it too
    const fonts = document.fonts;
    const heroImg = $('.hero-bg img');
    if (fonts) {
      Promise.all(['700 20px "Barlow Condensed"', '800 20px "Barlow Condensed"', '400 22px "Permanent Marker"'].map((f) => fonts.load(f).catch(() => {})))
        .then(() => Promise.all([fonts.ready, heroImg && heroImg.decode ? heroImg.decode().catch(() => {}) : null]))
        .then(() => {
          if (!active) return;
          const n = texName.image, u = texU.image;
          drawName(n.getContext('2d'), n.width, n.height); drawU(u.getContext('2d'), u.width, u.height);
          texName.needsUpdate = texU.needsUpdate = true;
          buildPosters(); assignFlicker(); lofiAll();
        });
    }
  }
}
